import { useEffect, useState, useCallback } from "react";
import { ShoppingCart, ExternalLink, Loader2, PackageCheck, Warehouse, Check } from "lucide-react";
import { fetchSpecialOrders, markJobOrdered, markJobReceived, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function ItemMeta({ item }) {
    const vare = [item.purchase_brand, item.purchase_requested_item].filter(Boolean).join(" · ");
    return (
        <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-900 truncate">{vare || "Specialbestilling"}</span>
                {item.manufacturer_part_number && (
                    <span className="text-[11px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">#{item.manufacturer_part_number}</span>
                )}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
                <span>Opgave: {item.job_title}</span>
                <span>· Kunde: {item.customer}</span>
                {item.selected_supplier && <span>· Leverandør: <strong className="text-slate-700">{item.selected_supplier}</strong></span>}
            </div>
        </div>
    );
}

// needs_purchase → vises under Indkøbsliste
export function SpecialNeedsSection() {
    const [items, setItems] = useState([]);
    const [orderItem, setOrderItem] = useState(null);
    const refresh = useCallback(async () => {
        try { const d = await fetchSpecialOrders(); setItems(d.needs_purchase || []); }
        catch { /* stille — sektionen skjules blot */ }
    }, []);
    useEffect(() => { refresh(); }, [refresh]);

    if (items.length === 0) return null;
    return (
        <section data-testid="special-needs-section" className="mt-5">
            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-3">
                <ShoppingCart className="w-4 h-4 text-red-600" /> Specialbestillinger fra AI-opkald
                <span className="text-xs bg-red-100 text-red-700 rounded-full px-2 py-0.5">{items.length}</span>
            </h3>
            <div className="space-y-2">
                {items.map((item) => (
                    <div key={item.job_id} data-testid={`special-needs-row-${item.job_id}`} className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-3 hover:border-red-200">
                        <ShoppingCart className="w-9 h-9 p-2 text-red-600 bg-red-50 rounded-lg shrink-0" />
                        <div className="flex-1"><ItemMeta item={item} /></div>
                        <div className="flex gap-1.5 shrink-0">
                            {item.selected_product_url && (
                                <a href={item.selected_product_url} target="_blank" rel="noreferrer" data-testid={`special-open-shop-${item.job_id}`}>
                                    <Button size="sm" variant="outline" className="h-8 text-xs"><ExternalLink className="w-3.5 h-3.5 mr-1" /> Åbn butik</Button>
                                </a>
                            )}
                            <Button size="sm" onClick={() => setOrderItem(item)} data-testid={`special-confirm-ordered-${item.job_id}`} className="h-8 text-xs bg-amber-500 hover:bg-amber-600 text-white">
                                <Check className="w-3.5 h-3.5 mr-1" /> Bekræft bestilt
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
            {orderItem && <ConfirmOrderDialog item={orderItem} onClose={() => setOrderItem(null)} onDone={() => { setOrderItem(null); refresh(); }} />}
        </section>
    );
}

function ConfirmOrderDialog({ item, onClose, onDone }) {
    const [salePrice, setSalePrice] = useState("");
    const [purchaseCost, setPurchaseCost] = useState("");
    const [saving, setSaving] = useState(false);
    const priceValid = salePrice !== "" && Number(salePrice) > 0;
    const submit = async () => {
        if (!priceValid) { toast.error("Salgspris til kunden er obligatorisk."); return; }
        setSaving(true);
        try {
            const payload = { sale_price: Number(salePrice) };
            if (purchaseCost !== "" && Number(purchaseCost) >= 0) payload.purchase_cost = Number(purchaseCost);
            await markJobOrdered(item.job_id, payload);
            toast.success("Bekræftet bestilt — flyttet til leveringscheckliste");
            onDone();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };
    return (
        <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle className="flex items-center gap-2 text-amber-700"><Check className="w-5 h-5" /> Bekræft bestilt</DialogTitle></DialogHeader>
                <div className="mt-2 text-sm text-slate-600">{[item.purchase_brand, item.purchase_requested_item].filter(Boolean).join(" · ")} · {item.customer}</div>
                <div className="mt-3 space-y-3">
                    <div>
                        <Label>Salgspris til kunden (kr) *</Label>
                        <Input data-testid="special-sale-price" type="number" min="0.01" step="0.01" value={salePrice} onChange={(e) => setSalePrice(e.target.value)} placeholder="Fx 4995" className="mt-1 h-10" autoFocus />
                        <p className="mt-1 text-[11px] text-slate-500">Obligatorisk — bruges som fakturalinje på opgaven.</p>
                    </div>
                    <div>
                        <Label>Din indkøbspris (kr) <span className="text-slate-400 text-xs">(intern, valgfri)</span></Label>
                        <Input data-testid="special-purchase-cost" type="number" min="0" step="0.01" value={purchaseCost} onChange={(e) => setPurchaseCost(e.target.value)} placeholder="Fx 3200" className="mt-1 h-10" />
                    </div>
                </div>
                <div className="mt-4 flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Annullér</Button>
                    <Button type="button" disabled={saving || !priceValid} onClick={submit} data-testid="special-confirm-order-submit" className="bg-amber-500 hover:bg-amber-600 text-white">
                        {saving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />} Bekræft bestilt
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ordered → vises under Leveringscheckliste
export function SpecialDeliverySection() {
    const [items, setItems] = useState([]);
    const [receiveItem, setReceiveItem] = useState(null);
    const refresh = useCallback(async () => {
        try { const d = await fetchSpecialOrders(); setItems(d.ordered || []); }
        catch { /* stille */ }
    }, []);
    useEffect(() => { refresh(); }, [refresh]);

    if (items.length === 0) return null;
    return (
        <section data-testid="special-delivery-section" className="mt-5">
            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-3">
                <ShoppingCart className="w-4 h-4 text-amber-600" /> Specialbestillinger fra AI-opkald
                <span className="text-xs bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">{items.length}</span>
            </h3>
            <div className="space-y-2">
                {items.map((item) => (
                    <div key={item.job_id} data-testid={`special-ordered-row-${item.job_id}`} className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-3 hover:border-amber-200">
                        <PackageCheck className="w-9 h-9 p-2 text-amber-600 bg-amber-50 rounded-lg shrink-0" />
                        <div className="flex-1"><ItemMeta item={item} /></div>
                        <div className="flex gap-1.5 shrink-0">
                            {item.selected_product_url && (
                                <a href={item.selected_product_url} target="_blank" rel="noreferrer" data-testid={`special-ordered-open-shop-${item.job_id}`}>
                                    <Button size="sm" variant="outline" className="h-8 text-xs"><ExternalLink className="w-3.5 h-3.5 mr-1" /> Åbn butik</Button>
                                </a>
                            )}
                            <Button size="sm" onClick={() => setReceiveItem(item)} data-testid={`special-mark-received-${item.job_id}`} className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white">
                                <PackageCheck className="w-3.5 h-3.5 mr-1" /> Marker som modtaget
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
            {receiveItem && <ReceiveDialog item={receiveItem} onClose={() => setReceiveItem(null)} onDone={() => { setReceiveItem(null); refresh(); }} />}
        </section>
    );
}

function ReceiveDialog({ item, onClose, onDone }) {
    const [loc, setLoc] = useState("");
    const [saving, setSaving] = useState(false);
    const submit = async () => {
        if (!loc.trim()) { toast.error("Lagerplacering er obligatorisk."); return; }
        setSaving(true);
        try {
            await markJobReceived(item.job_id, { inventory_location: loc.trim() });
            toast.success("Modtaget — varen er nu på lager og jobbet er klar til udførelse");
            onDone();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };
    return (
        <Dialog open onOpenChange={(o) => !o && !saving && onClose()}>
            <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle className="flex items-center gap-2 text-emerald-700"><Warehouse className="w-5 h-5" /> Marker som modtaget</DialogTitle></DialogHeader>
                <div className="mt-2 text-sm text-slate-600">{[item.purchase_brand, item.purchase_requested_item].filter(Boolean).join(" · ")} · {item.customer}</div>
                <div className="mt-3">
                    <Label>Lagerplacering *</Label>
                    <Input data-testid="special-inventory-location" value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="Fx Lager A, Reol 3, Bil 2" maxLength={120} className="mt-1 h-10" autoFocus />
                    <p className="mt-1 text-[11px] text-slate-500">Obligatorisk — hvor er varen placeret indtil udførelse?</p>
                </div>
                <div className="mt-4 flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Annullér</Button>
                    <Button type="button" disabled={saving || !loc.trim()} onClick={submit} data-testid="special-receive-submit" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                        {saving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />} Bekræft modtaget
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
